import React from "react";
import ReactDOM from "react-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddSwipeDeckToSectionModal from "./AddSwipeDeckToSectionModal";

/**
 * BlockNote block for swipe decks. Renders an inline tappable hero card
 * inside the section content; tapping opens the fullscreen player.
 *
 * The player bundle (~66KB gzipped: framer-motion + embla-carousel-react +
 * canvas-confetti) is lazy-loaded so employees who never tap a deck
 * don't pay the download cost. First tap kicks off the chunk fetch and
 * shows a brief loading state.
 *
 * Used in three modes:
 *   - employee mode (mode="complete"): inline hero card → tap opens player
 *   - admin authoring (mode="edit"): if no deck selected, shows a
 *     placeholder picker; once selected, same hero card so the admin can
 *     preview by tapping
 *   - read-only preview (mode="preview"): hero card, tap opens player
 */
const SwipeDeckPlayerLazy = React.lazy(() =>
  import(
    "../take_training_employee/swipe_deck_player/SwipeDeckPlayer"
  ),
);

export function SwipeDeck(props) {
  const {
    block,
    editor,
    homeCompany,
    employeeID,
    language,
    sectionUUID,
    setRefreshSection,
    mode,
  } = props;

  // Prefer the drag-proof primitive props; fall back to the legacy object prop
  // (swipeDeckSelected). BlockNote wipes object props on drag/copy-paste but
  // keeps strings, so the deck stays configured; the backend re-hydrates the
  // full object (name, cards_count, completion) on read.
  const swipeDeckSelected = block?.props?.swipeDeckSelected || {};
  const deckUuid = block?.props?.swipeDeckUuid || swipeDeckSelected.uuid || null;
  const deckName = block?.props?.swipeDeckName || swipeDeckSelected.name || "";
  const userHasCompleted = !!swipeDeckSelected.user_has_completed;
  const cardsCount =
    typeof swipeDeckSelected.cards_count === "number"
      ? swipeDeckSelected.cards_count
      : null;

  const [showPlayer, setShowPlayer] = React.useState(false);
  const [showPickerModal, setShowPickerModal] = React.useState(false);

  const isEditMode = mode === "edit";

  // Prefetch the lazy player chunk as soon as the block mounts. Without
  // this, the first tap pays for the chunk fetch + Suspense fallback,
  // which reads as a "Loading… → flash → deck" glitch. Mirrors what
  // SwipeDeckInputsModal does for its Preview button. Same import path
  // as the React.lazy declaration above; the bundler dedupes to one
  // chunk so this is just a warm-up, not a second download.
  React.useEffect(() => {
    import(
      "../take_training_employee/swipe_deck_player/SwipeDeckPlayer"
    );
  }, []);

  const handleDeckSelect = (deck) => {
    if (!deck || !editor || !block) return;
    // Persist only the fields the player + inline hero need so the
    // BlockNote prop stays small. Name comes through for the empty-
    // state-no-more label after selection; uuid is what the player
    // fetches against.
    editor.updateBlock(block, {
      ...block,
      props: {
        ...block.props,
        swipeDeckSelected: {
          uuid: deck.uuid,
          name: deck.name || "",
        },
        // Drag-proof primitive mirror (object props are wiped on drag).
        swipeDeckUuid: deck.uuid || "",
        swipeDeckName: deck.name || "",
      },
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Localized labels

  const lang = (language || "").toLowerCase();
  // Fallback title only — when the deck has been bound, we use its actual
  // name so the inline hero matches what the admin authored.
  const titleText =
    lang === "spanish"
      ? "Tarjetas de aprendizaje"
      : lang === "chinese"
      ? "学习卡片"
      : "Swipe Deck";
  // Card count label — pluralized in English/Spanish, fixed unit in Chinese.
  const cardsCountText =
    cardsCount != null
      ? lang === "spanish"
        ? `${cardsCount} ${cardsCount === 1 ? "tarjeta" : "tarjetas"}`
        : lang === "chinese"
        ? `${cardsCount} 张卡片`
        : `${cardsCount} ${cardsCount === 1 ? "card" : "cards"}`
      : null;
  // Action affordance — shifts to "Tap to replay" once the user has
  // completed the deck so the inline card visually distinguishes done
  // vs. not-done.
  const actionText = userHasCompleted
    ? lang === "spanish"
      ? "Pulsa para repetir"
      : lang === "chinese"
      ? "点击重玩"
      : "Tap to replay"
    : lang === "spanish"
    ? "Pulsa para empezar"
    : lang === "chinese"
    ? "点击开始"
    : "Tap to begin";
  // Combined subtitle: "5 cards · Tap to begin" (info before action). Falls
  // back to action-only when cards_count isn't hydrated yet.
  const subtitleText = cardsCountText
    ? `${cardsCountText} · ${actionText}`
    : actionText;
  const completedLabel =
    lang === "spanish"
      ? "Completado"
      : lang === "chinese"
      ? "已完成"
      : "Completed";
  const notConfiguredText =
    lang === "spanish"
      ? "Tarjeta de deslizamiento no configurada"
      : lang === "chinese"
      ? "未配置滑动卡片"
      : "Swipe deck not configured";
  const loadingText =
    lang === "spanish"
      ? "Cargando…"
      : lang === "chinese"
      ? "加载中…"
      : "Loading…";
  const editLabel =
    lang === "spanish" ? "Editar" : lang === "chinese" ? "编辑" : "Edit";

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Empty / unconfigured state

  if (!deckUuid) {
    // In edit mode, the placeholder is the entry point to the deck
    // picker — without this, admins can drop a swipeDeck block from the
    // slash menu but have no way to bind it to an actual deck. In other
    // modes (employee, preview), the empty state is just informational.
    if (isEditMode) {
      return (
        <>
          <button
            type="button"
            contentEditable={false}
            onClick={() => setShowPickerModal(true)}
            className="my-3 w-full p-4 border-2 border-dashed border-violet-300 rounded-xl text-sm text-violet-700 hover:bg-violet-50 hover:border-violet-500 transition-colors flex items-center justify-center gap-2 cursor-pointer focus:outline-none"
          >
            <StyleOutlinedIcon style={{ fontSize: 18 }} />
            <span>Select a swipe deck</span>
          </button>
          <AddSwipeDeckToSectionModal
            open={showPickerModal}
            handleClose={() => setShowPickerModal(false)}
            handleSwipeDeckSelect={handleDeckSelect}
            homeCompany={homeCompany}
          />
        </>
      );
    }
    return (
      <div
        className="my-3 p-4 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 text-center"
        contentEditable={false}
      >
        {notConfiguredText}
      </div>
    );
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Inline hero card

  return (
    <>
      <button
        type="button"
        onClick={() => setShowPlayer(true)}
        contentEditable={false}
        className="my-3 group relative w-full overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
        style={{
          // Brand-violet gradient — the inline preview signals novelty.
          background:
            "linear-gradient(135deg, #6d28d9 0%, #4c1d95 60%, #1e1b4b 100%)",
          minHeight: 132,
        }}
      >
        {/* Subtle decorative ring — Stories-style "this is new" cue */}
        <div
          aria-hidden="true"
          className="absolute -top-12 -right-12 w-36 h-36 rounded-full opacity-25"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-16 -left-12 w-40 h-40 rounded-full opacity-15"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)",
          }}
        />

        {isEditMode && deckUuid && props.onEditAsset && (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              props.onEditAsset(deckUuid);
            }}
            className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium hover:bg-white/30 transition-colors cursor-pointer"
          >
            <EditOutlinedIcon style={{ fontSize: 14 }} />
            {editLabel}
          </div>
        )}

        <div className="relative flex items-center gap-4 px-6 py-5 text-white text-left">
          {/* Play affordance */}
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors">
            <PlayArrowIcon style={{ fontSize: 26 }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-lg font-semibold leading-tight truncate">
                {/* Show the bound deck's actual name (e.g. "Menu
                    Familiarization Essentials") in all modes, mirroring
                    how the Quiz block surfaces its quiz name. Fall back
                    to the generic localized title only when nothing is
                    bound (mid-load, deleted deck, etc.). */}
                {deckName || titleText}
              </div>
              {/* Completion is per user+section and only meaningful to a
                  learner. In authoring (edit) and track/module preview the
                  backend still re-hydrates the viewing admin's own
                  user_has_completed, so gate the badge to the learner
                  (complete) context to avoid showing "Completed" in the editor. */}
              {mode === "complete" && userHasCompleted && (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                  {completedLabel}
                </span>
              )}
            </div>
            <div className="text-sm opacity-85 mt-0.5">{subtitleText}</div>
          </div>
        </div>
      </button>

      {showPlayer &&
        // Portal to body so the fixed-positioned player escapes any
        // ancestor with a `transform` (e.g. SectionInputsModal2's Box
        // uses `translate(-50%, -50%)`, which would otherwise become
        // the containing block for `position: fixed` and clip the
        // fullscreen overlay inside the parent modal).
        ReactDOM.createPortal(
          <React.Suspense
            fallback={
              <div className="fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center">
                <div className="text-white/80 text-sm">{loadingText}</div>
              </div>
            }
          >
            <SwipeDeckPlayerLazy
              deckUuid={deckUuid}
              sectionUuid={sectionUUID}
              companyId={homeCompany}
              employeeID={employeeID}
              language={language}
              // Admin contexts (mode="edit" in section authoring,
              // mode="preview" in module/track preview) have no real
              // section embedding, so the employee fetch path would bail
              // on missing sectionUuid and leave the loader spinning.
              // previewMode flips useSwipeDeck to the admin endpoint
              // (no section required, includes answer keys for local
              // correctness). Default to non-preview when mode is
              // missing so legacy CompleteTrack callers (which pass
              // mode="complete") keep their employee-path behavior.
              // Mirrors Quiz's `type="Preview"` flag in CompleteQuizModal.
              previewMode={mode === "edit" || mode === "preview"}
              onClose={() => {
                setShowPlayer(false);
                // Refresh the parent section so can_complete re-evaluates and
                // the "Mark Section Complete" button reflects the new state.
                if (setRefreshSection) setRefreshSection(true);
              }}
              onCompleted={() => {
                // No-op for v1 — onClose's setRefreshSection handles
                // the parent state refresh. Hook stays exposed for any
                // future "in-place completion badge" UI on the inline card.
              }}
            />
          </React.Suspense>,
          document.body,
        )}
    </>
  );
}
